/**
 * SidebarOverlay Component
 * Slide-in navigation menu with search functionality
 * Phase 2: Navigation - UI Architecture Restructure
 * Phase 4: Polish - Animations, responsive, accessibility
 * Phase 6: Badge Model Permission Alignment
 *
 * Design tokens used (from data.yaml):
 * - Animation: 300ms (slow) with ease-in-out
 * - Touch targets: min 44px for mobile
 * - Spacing: p-4 (16px), p-6 (24px)
 *
 * Badge Model Permissions:
 * - advisor:access -> Advisor Portal menu
 * - team:view -> Team Management menu
 * - user:manage / users:read -> User Management menu
 * - roles:read -> NocoBase Admin link (external)
 */

import { useEffect, useRef, useState, useCallback, useMemo } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
  X,
  Search,
  House,
  Shield,
  Briefcase,
  ChevronRight,
  ChevronDown,
  LayoutDashboard,
  FileText,
  DollarSign,
  Package,
  Users,
  UsersRound,
  ExternalLink,
} from 'lucide-react'
import { usePermissions } from '@/hooks/usePermissions'
import { useAnimatedOverlay } from './useAnimatedOverlay'

// UI copy
const translations = {
  search: 'Search menu…',
  close: 'Close menu',
  noResults: 'No results',
  mainNavigation: 'Main navigation',
}

// Submenu item type
interface SubMenuItem {
  path: string
  label: string
  labelEn: string
  icon: typeof House
}

// Menu item type with optional submenu and permission
interface MenuItem {
  path: string
  label: string
  labelEn: string
  icon: typeof House
  submenu?: SubMenuItem[]
  permission?: string // Badge Model permission required
  anyPermissions?: string[] // Any of these permissions required
  hideIfPermission?: string // Hide this item if the user holds this permission (avoids duplicate entries)
  external?: boolean // Opens in new tab (e.g., NocoBase admin)
}

// Advisor submenu items (all require advisor:access)
const advisorSubmenu: SubMenuItem[] = [
  {
    path: '/dashboard',
    label: 'แดชบอร์ด',
    labelEn: 'Dashboard',
    icon: LayoutDashboard,
  },
  {
    path: '/cases',
    label: 'Cases',
    labelEn: 'Cases',
    icon: FileText,
  },
  {
    path: '/cases?filter=application',
    label: 'Applications',
    labelEn: 'Applications',
    icon: FileText,
  },
  // Policies + Commission hidden until real data is ready
]

// Paths that belong to the advisor menu group (for auto-expand and highlight)
const ADVISOR_PATHS = ['/dashboard', '/cases', '/policies', '/commission', '/clients', '/advisor']

// Full menu items configuration (permissions handled dynamically)
const allMenuItems: MenuItem[] = [
  {
    path: '/dashboard',
    label: 'Advisor',
    labelEn: 'Advisor Portal',
    icon: Briefcase,
    submenu: advisorSubmenu,
    permission: 'advisor:access',
  },
  // Standalone Cases entry for case participants WITHOUT the Advisor Portal group
  // (e.g. WRISE) — advisor/admin already reach Cases via the Advisor submenu above.
  {
    path: '/cases',
    label: 'Cases',
    labelEn: 'Cases',
    icon: FileText,
    permission: 'client_applications:read',
    hideIfPermission: 'advisor:access',
  },
  // Admin Back-Office (Badge Model permissions)
  {
    path: '/commission-entry',
    label: 'บันทึก Commission',
    labelEn: 'Commission Entry',
    icon: DollarSign,
    permission: 'commission:entry', // Badge Model: finance
  },
  {
    path: '/product-management',
    label: 'จัดการผลิตภัณฑ์',
    labelEn: 'Product Management',
    icon: Package,
    permission: 'product:manage', // Badge Model: product-admin
  },
  // Team Management (Spec A) — Users + Advisors + Teams under one group.
  {
    path: '/admin',
    label: 'ทีมงาน',
    labelEn: 'Team',
    icon: Users,
    submenu: [
      { path: '/admin/users', label: 'ผู้ใช้งาน', labelEn: 'Users', icon: Users },
      { path: '/admin/advisor-registrations', label: 'ตัวแทน', labelEn: 'Advisors', icon: Briefcase },
      { path: '/admin/teams', label: 'ทีม', labelEn: 'Teams', icon: UsersRound },
    ],
    anyPermissions: ['users:read', 'advisor_registrations:manage', 'advisor_registrations:approve', 'teams:manage'],
  },
  // Team leader's own team page
  {
    path: '/team',
    label: 'ทีมงาน',
    labelEn: 'Team',
    icon: Users,
    permission: 'team:view',
  },
  {
    path: 'https://operation.ifcg.co.th/admin/',
    label: 'จัดการบทบาท (NocoBase)',
    labelEn: 'Roles & Permissions',
    icon: Shield,
    permission: 'roles:read',
    external: true,
  },
]

interface SidebarOverlayProps {
  /** Whether the sidebar is open */
  isOpen: boolean
  /** Callback when sidebar should close */
  onClose: () => void
}

export function SidebarOverlay({ isOpen, onClose }: SidebarOverlayProps) {
  const location = useLocation()
  const { can } = usePermissions()
  const [searchQuery, setSearchQuery] = useState('')
  const { mounted: isVisible, animateIn: isAnimating } = useAnimatedOverlay(isOpen, 300)
  const [expandedMenus, setExpandedMenus] = useState<Set<string>>(new Set())
  const sidebarRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const previousActiveElement = useRef<HTMLElement | null>(null)
  const prevMounted = useRef(false)

  // Filter menu items based on user permissions (Badge Model)
  const menuItems = useMemo(() => {
    return allMenuItems.filter((item) => {
      // Exclusion: hide if the user holds the excluding permission (dedup, e.g. Cases for advisor/admin)
      if (item.hideIfPermission && can(item.hideIfPermission)) return false

      // No permission required - show to all
      if (!item.permission && !item.anyPermissions) return true

      // Single permission check
      if (item.permission) {
        return can(item.permission)
      }

      // Any of the permissions check
      if (item.anyPermissions) {
        return item.anyPermissions.some((p) => can(p))
      }

      return false
    })
  }, [can])

  // Auto-expand menus if on their routes
  useEffect(() => {
    if (ADVISOR_PATHS.some((p) => location.pathname.startsWith(p))) {
      setExpandedMenus((prev) => new Set([...prev, '/dashboard']))
    }
    if (location.pathname.startsWith('/admin/users') || location.pathname.startsWith('/admin/advisor-registrations') || location.pathname.startsWith('/admin/teams')) {
      setExpandedMenus((prev) => new Set([...prev, '/admin']))
    }
  }, [location.pathname])

  // Toggle submenu expansion
  const toggleSubmenu = (path: string) => {
    setExpandedMenus((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(path)) {
        newSet.delete(path)
      } else {
        newSet.add(path)
      }
      return newSet
    })
  }

  // Filter menu items based on search query (including submenu items)
  const filteredItems = menuItems.filter((item) => {
    if (!searchQuery) return true
    const query = searchQuery.toLowerCase()
    const matchesParent =
      item.label.toLowerCase().includes(query) ||
      item.labelEn.toLowerCase().includes(query)
    const matchesSubmenu = item.submenu?.some(
      (sub) =>
        sub.label.toLowerCase().includes(query) ||
        sub.labelEn.toLowerCase().includes(query)
    )
    return matchesParent || matchesSubmenu
  })

  // Check if submenu item matches search
  const getFilteredSubmenu = (submenu: SubMenuItem[] | undefined) => {
    if (!submenu) return []
    if (!searchQuery) return submenu
    const query = searchQuery.toLowerCase()
    return submenu.filter(
      (sub) =>
        sub.label.toLowerCase().includes(query) ||
        sub.labelEn.toLowerCase().includes(query)
    )
  }

  // Handle escape key to close
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    },
    [onClose]
  )

  // Close on route change
  useEffect(() => {
    if (isOpen) {
      onClose()
    }
    // Only trigger when location changes, not when isOpen changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname])

  // Focus capture + scroll-lock while the sidebar is open (animation cycle
  // is handled by useAnimatedOverlay).
  useEffect(() => {
    if (!isOpen) return
    // Store current active element for focus restoration
    previousActiveElement.current = document.activeElement as HTMLElement
    // Focus search input after animation
    const focusTimer = setTimeout(() => {
      searchInputRef.current?.focus()
    }, 300) // Match animation duration
    // Prevent body scroll
    document.body.style.overflow = 'hidden'
    return () => {
      clearTimeout(focusTimer)
      document.body.style.overflow = ''
    }
  }, [isOpen])

  // Reset search + restore focus when the overlay finishes closing and leaves
  // the DOM (mirrors the prior in-timer close callback).
  useEffect(() => {
    if (prevMounted.current && !isVisible) {
      setSearchQuery('')
      previousActiveElement.current?.focus()
    }
    prevMounted.current = isVisible
  }, [isVisible])

  // Add keyboard event listener
  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown)
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, handleKeyDown])

  // Handle click outside
  const handleBackdropClick = (event: React.MouseEvent) => {
    if ((event.target as HTMLElement).dataset.backdrop === 'true') {
      onClose()
    }
  }

  // Focus trap - cycle focus within sidebar
  const handleTabKey = (event: React.KeyboardEvent) => {
    if (event.key !== 'Tab') return

    const focusableElements = sidebarRef.current?.querySelectorAll<HTMLElement>(
      'button, [href], input, [tabindex]:not([tabindex="-1"])'
    )
    if (!focusableElements || focusableElements.length === 0) return

    const firstElement = focusableElements[0]
    const lastElement = focusableElements[focusableElements.length - 1]

    if (event.shiftKey) {
      // Shift + Tab
      if (document.activeElement === firstElement) {
        event.preventDefault()
        lastElement?.focus()
      }
    } else {
      // Tab
      if (document.activeElement === lastElement) {
        event.preventDefault()
        firstElement?.focus()
      }
    }
  }

  // Don't render if not visible
  if (!isVisible) return null

  return (
    <div
      className="fixed inset-0 z-50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="sidebar-title"
      onClick={handleBackdropClick}
      onKeyDown={handleTabKey}
    >
      {/* Backdrop - opacity controlled by isAnimating */}
      <div
        data-backdrop="true"
        className={`
          absolute inset-0 bg-black/50
          transition-opacity duration-300 ease-in-out
          motion-reduce:transition-none
          ${isAnimating ? 'opacity-100' : 'opacity-0'}
        `}
        aria-hidden="true"
      />

      {/* Sidebar Panel - transform controlled by isAnimating */}
      <div
        ref={sidebarRef}
        className={`
          absolute left-0 top-0 h-full w-80 max-w-[85vw]
          bg-background shadow-xl
          transform transition-transform duration-300 ease-[cubic-bezier(0.19,1,0.22,1)]
          motion-reduce:transition-none motion-reduce:transform-none
          ${isAnimating ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b-2 border-border-subtle px-4 py-4">
          <span id="sidebar-title" className="text-lg font-semibold text-foreground">
            Triple A
          </span>
          {/* Close button - 44px touch target for accessibility */}
          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            className="
              flex h-11 w-11 items-center justify-center rounded-lg
              text-foreground-muted hover:bg-background-muted hover:text-foreground
              transition-colors duration-150
              focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2
            "
            aria-label={translations.close}
          >
            <X className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>

        {/* Search Input */}
        <div className="p-4">
          <div className="relative">
            <Search
              className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-foreground-subtle"
              aria-hidden="true"
            />
            <input
              ref={searchInputRef}
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={translations.search}
              className="input pl-10 h-11"
              aria-label={translations.search}
            />
          </div>
        </div>

        {/* Menu Items */}
        <nav className="px-2 flex-1 overflow-y-auto" aria-label={translations.mainNavigation}>
          {filteredItems.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-foreground-subtle" role="status">
              {translations.noResults}
            </p>
          ) : (
            <ul className="space-y-1" role="list">
              {filteredItems.map((item) => {
                const Icon = item.icon
                const isActive = location.pathname === item.path || (item.path === '/team' && location.pathname.startsWith('/team/'))
                const hasSubmenu = item.submenu && item.submenu.length > 0
                const isExpanded = expandedMenus.has(item.path) || (searchQuery && hasSubmenu)
                const filteredSubmenu = getFilteredSubmenu(item.submenu)
                const isInSubmenuRoute = item.submenu
                  ? item.submenu.some((sub) => location.pathname.startsWith(sub.path))
                  : location.pathname.startsWith(item.path)

                return (
                  <li key={item.path}>
                    {hasSubmenu ? (
                      <>
                        {/* Parent item with submenu - toggle button */}
                        <button
                          type="button"
                          onClick={() => toggleSubmenu(item.path)}
                          className={`
                            w-full flex items-center gap-3 rounded-lg px-4 py-3 min-h-[44px]
                            text-sm font-medium transition-colors duration-150
                            focus:outline-none focus:ring-2 focus:ring-primary focus:ring-inset
                            ${isInSubmenuRoute
                              ? 'bg-primary/10 text-primary'
                              : 'text-foreground hover:bg-background-muted'
                            }
                          `}
                          aria-expanded={isExpanded ? true : false}
                          aria-controls={`submenu-${item.path}`}
                        >
                          <Icon className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
                          <span className="flex-1 text-left">{item.labelEn}</span>
                          <ChevronDown
                            className={`
                              h-4 w-4 transition-transform duration-150
                              ${isExpanded ? 'rotate-180' : ''}
                            `}
                            aria-hidden="true"
                          />
                        </button>

                        {/* Submenu items */}
                        <ul
                          id={`submenu-${item.path}`}
                          className={`
                            ml-4 mt-1 space-y-1 overflow-hidden
                            transition-[max-height,opacity] duration-150 ease-in-out
                            ${isExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}
                          `}
                          role="list"
                        >
                          {filteredSubmenu.map((subItem) => {
                            const SubIcon = subItem.icon
                            const isSubActive = location.pathname === subItem.path

                            return (
                              <li key={subItem.path}>
                                <Link
                                  to={subItem.path}
                                  className={`
                                    flex items-center gap-3 rounded-lg px-4 py-2.5 min-h-[40px]
                                    text-sm transition-colors duration-150
                                    focus:outline-none focus:ring-2 focus:ring-primary focus:ring-inset
                                    ${isSubActive
                                      ? 'bg-primary text-white font-medium'
                                      : 'text-foreground-muted hover:bg-background-muted hover:text-foreground'
                                    }
                                  `}
                                  aria-current={isSubActive ? 'page' : undefined}
                                >
                                  <SubIcon className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
                                  <span className="flex-1">{subItem.labelEn}</span>
                                  <ChevronRight
                                    className={`
                                      h-3 w-3 transition-transform duration-150
                                      ${isSubActive ? 'translate-x-1 opacity-100' : 'opacity-0'}
                                    `}
                                    aria-hidden="true"
                                  />
                                </Link>
                              </li>
                            )
                          })}
                        </ul>
                      </>
                    ) : item.external ? (
                      /* External link (opens in new tab) */
                      <a
                        href={item.path}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="
                          flex items-center gap-3 rounded-lg px-4 py-3 min-h-[44px]
                          text-sm font-medium transition-colors duration-150
                          text-foreground hover:bg-background-muted
                          focus:outline-none focus:ring-2 focus:ring-primary focus:ring-inset
                        "
                      >
                        <Icon className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
                        <span className="flex-1">{item.labelEn}</span>
                        <ExternalLink className="h-4 w-4 opacity-50" aria-hidden="true" />
                      </a>
                    ) : (
                      /* Regular menu item without submenu */
                      <Link
                        to={item.path}
                        className={`
                          flex items-center gap-3 rounded-lg px-4 py-3 min-h-[44px]
                          text-sm font-medium transition-colors duration-150
                          focus:outline-none focus:ring-2 focus:ring-primary focus:ring-inset
                          ${isActive
                            ? 'bg-primary text-white'
                            : 'text-foreground hover:bg-background-muted'
                          }
                        `}
                        aria-current={isActive ? 'page' : undefined}
                      >
                        <Icon className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
                        <span className="flex-1">{item.labelEn}</span>
                        <ChevronRight
                          className={`
                            h-4 w-4 transition-transform duration-150
                            ${isActive ? 'translate-x-1 opacity-100' : 'opacity-0'}
                          `}
                          aria-hidden="true"
                        />
                      </Link>
                    )}
                  </li>
                )
              })}
            </ul>
          )}
        </nav>

        {/* Footer - App version or help link */}
        <div className="absolute bottom-0 left-0 right-0 border-t-2 border-border-subtle p-4 bg-background">
          <p className="text-xs text-foreground-subtle text-center">
            Triple A Internal System v1.0
          </p>
        </div>
      </div>
    </div>
  )
}

export default SidebarOverlay
